import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  Checkbox,
  FormControlLabel,
  Link,
} from '@material-ui/core';
import { usePostHog } from 'posthog-js/react';
import { useLazyQuery } from '@apollo/client';

import {
  FormContainer,
  Title,
  Subtitle,
  CheckboxContainer,
  CheckboxLabel,
  MarketingConsentLabel,
  SubmitButtonWrapper,
  inputLabelStyle,
  inputStyle,
  checkboxStyle,
  submitButtonStyle,
  helperTextErrorStyle,
} from './ConfirmDetails.styles';

import { SpringHealthFormData } from '../../SpringHealthEnrollment';

import Button from '@/components/Button';
import TextField from '@/components/TextField';
import { Product, getProductTermsAndConditions, getProductPrivacyPolicy } from '@/utils/productHelpers';
import { getProgram } from '@/utils/posthog';
import { useSpringId } from '@/hooks/useSpringId';
import useSessionTimeout from '@/hooks/useSessionTimeout';
import { useEnrollPatientPlatgen } from '@/hooks/useEnrollPatientPlatgen';
import { useEnrollSleepioSpringHealth } from '@/hooks/useEnrollSleepioSpringHealth';
import { QUERY_DISTINCT_ID_WITH_EMAIL } from '@/apollo/queries';

type FormFieldProps = {
  name: keyof SpringHealthFormData;
  testId: string;
  label: string;
  placeholder: string;
  required?: boolean;
  type?: string;
  InputProps?: Record<string, unknown>;
  onBlurExtra?: () => void;
};

const FormField: React.FC<FormFieldProps> = ({
  name,
  testId,
  label,
  placeholder,
  required,
  type,
  InputProps: inputPropsOverride,
  onBlurExtra,
}) => {
  const { control, formState: { errors }, clearErrors } = useFormContext<SpringHealthFormData>();

  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => (
        <TextField
          {...field}
          onBlur={() => {
            field.onBlur();
            onBlurExtra?.();
          }}
          data-testid={testId}
          required={required}
          label={label}
          placeholder={placeholder}
          type={type}
          helperText={errors?.[name]?.message}
          error={!!errors?.[name]}
          onFocus={() => clearErrors(name)}
          InputLabelProps={{ shrink: true, style: inputLabelStyle }}
          InputProps={{ style: inputStyle, ...inputPropsOverride }}
          FormHelperTextProps={{ style: errors?.[name] ? helperTextErrorStyle : undefined }}
        />
      )}
    />
  );
};

type Props = {
  product: Product;
  onSuccess: () => void;
  onError: () => void;
};

const ConfirmDetails: React.FC<Props> = ({ product, onSuccess, onError }) => {
  const { t } = useTranslation();
  const posthog = usePostHog();
  const { springId, sessionId } = useSpringId();
  const { isExpired } = useSessionTimeout();
  const { enroll: enrollDaylight, loading: loadingDaylight } = useEnrollPatientPlatgen();
  const { enroll: enrollSleepio, loading: loadingSleepio } = useEnrollSleepioSpringHealth();
  const [getDistinctId] = useLazyQuery(QUERY_DISTINCT_ID_WITH_EMAIL, {
    fetchPolicy: 'network-only',
  });

  const isSleepio = product === 'sleepio';
  const enroll = isSleepio ? enrollSleepio : enrollDaylight;
  const loading = isSleepio ? loadingSleepio : loadingDaylight;
  const {
    formState: { isValid },
    setError,
    clearErrors,
    handleSubmit,
    getValues,
  } = useFormContext<SpringHealthFormData>();

  const program = getProgram(product);

  const onSubmit = async (data: SpringHealthFormData) => {
    if (isExpired) {
      return;
    }

    try {
      const response = await enroll({
        email: data.email,
        firstName: data.firstName,
        lastName: data.lastName,
        password: data.password,
        phone: data.phoneNumber,
        product,
        springId,
      });

      const success = isSleepio
        ? response.data?.enrollDirectAccessPatient?.success
        : response.data?.enrollPatientPlatgen?.success;

      const patientUid = isSleepio
        ? response.data?.enrollDirectAccessPatient?.patientUid
        : response.data?.enrollPatientPlatgen?.patientUid;

      if (success) {
        // Fetch the user's PostHog distinct ID from our backend using their email,
        // then call posthog.identify() to link all future events to this user.
        // This allows PostHog to track user behavior across sessions and devices.
        try {
          const { data: idData } = await getDistinctId({
            variables: { email: data.email },
          });

          if (idData?.posthogDistinctIdWithEmail?.distinctId) {
            posthog?.identify(idData.posthogDistinctIdWithEmail.distinctId, {
              email: data.email,
            });
          }
        } catch (error) {
          console.error('Error fetching PostHog distinct ID:', error);
        }

        posthog?.capture('ActivationFlowSubmitted', {
          ...(isSleepio
            ? { 'wellness.cortex_id': patientUid }
            : { 'wellness.platgen_id': patientUid }),
          spring_member_id: springId,
          session_id: sessionId,
          program,
          product,
        });

        onSuccess();
        return;
      }

      onError();
    } catch (e) {
      onError();
    }
  };

  return (
    <FormContainer>
      <Title>
        {t('coverage.spring_health.confirm_details.title')}
      </Title>
      <Subtitle>
        {t(isSleepio
          ? 'coverage.spring_health.confirm_details.subtitle_sleepio'
          : 'coverage.spring_health.confirm_details.subtitle')}
      </Subtitle>

      <FormField
        name="firstName"
        testId="first-name-input"
        required
        label={t('coverage.spring_health.confirm_details.f_name_label')}
        placeholder={t('coverage.spring_health.confirm_details.f_name_placeholder')}
      />

      <FormField
        name="lastName"
        testId="last-name-input"
        required
        label={t('coverage.spring_health.confirm_details.l_name_label')}
        placeholder={t('coverage.spring_health.confirm_details.l_name_placeholder')}
      />

      <FormField
        name="zipCode"
        testId="zip-code-input"
        required
        label={t('coverage.spring_health.confirm_details.zipcode_label')}
        placeholder={t('coverage.spring_health.confirm_details.zipcode_placeholder')}
      />

      <FormField
        name="email"
        testId="email-input"
        required
        label={t('coverage.spring_health.confirm_details.email_label')}
        placeholder={t('coverage.spring_health.confirm_details.email_placeholder')}
      />

      <FormField
        name="phoneNumber"
        testId="phone-input"
        label={t('coverage.spring_health.confirm_details.phone_label')}
        placeholder={t('coverage.spring_health.confirm_details.phone_placeholder')}
      />

      <FormField
        name="password"
        testId="password-input"
        required
        label={t('coverage.spring_health.confirm_details.password_label')}
        placeholder={t('coverage.spring_health.confirm_details.password_placeholder')}
        type="password"
        onBlurExtra={() => {
          setTimeout(() => {
            const confirmPwd = getValues('confirmPassword');
            if (confirmPwd) {
              if (getValues('password') !== confirmPwd) {
                setError('confirmPassword', {
                  type: 'manual',
                  message: t('coverage.spring_health.errors.passwords_must_match'),
                });
              } else {
                clearErrors('confirmPassword');
              }
            }
          }, 0);
        }}
      />

      <FormField
        name="confirmPassword"
        testId="confirm-password-input"
        required
        label={t('coverage.spring_health.confirm_details.confirm_password_label')}
        placeholder={t('coverage.spring_health.confirm_details.confirm_password_placeholder')}
        type="password"
        onBlurExtra={() => {
          setTimeout(() => {
            const confirmPwd = getValues('confirmPassword');
            if (!confirmPwd) { return; }

            if (getValues('password') !== confirmPwd) {
              setError('confirmPassword', {
                type: 'manual',
                message: t('coverage.spring_health.errors.passwords_must_match'),
              });
            } else {
              clearErrors('confirmPassword');
            }
          }, 0);
        }}
      />

      <CheckboxContainer>
        <Controller
          name="acceptTerms"
          render={({ field }) => (
            <FormControlLabel
              style={{ marginBottom: 8, alignItems: 'flex-start' }}
              label={(
                <CheckboxLabel>
                  {t('coverage.spring_health.confirm_details.accept_terms_prefix')}
                  {' '}
                  <Link
                    href={getProductPrivacyPolicy(product)}
                    target="_blank"
                    rel="noopener noreferrer"
                    color="inherit"
                    underline="always"
                  >
                    {t('coverage.spring_health.confirm_details.privacy_policy')}
                  </Link>
                  {' '}
                  {t('coverage.spring_health.confirm_details.terms_and')}
                  {' '}
                  <Link
                    href={getProductTermsAndConditions(product)}
                    target="_blank"
                    rel="noopener noreferrer"
                    color="inherit"
                    underline="always"
                  >
                    {t('coverage.spring_health.confirm_details.terms_of_use')}
                  </Link>
                </CheckboxLabel>
              )}
              control={(
                <Checkbox
                  checked={!!field.value}
                  onChange={(e) => field.onChange(e.target.checked)}
                  data-testid="accept-terms-checkbox"
                  style={checkboxStyle}
                />
              )}
            />
          )}
        />

        <Controller
          name="smsConsent"
          render={({ field }) => (
            <FormControlLabel
              style={{ marginBottom: 8, alignItems: 'flex-start' }}
              label={(
                <CheckboxLabel>
                  {t('coverage.spring_health.confirm_details.sms_consent')}
                </CheckboxLabel>
              )}
              control={(
                <Checkbox
                  checked={!!field.value}
                  onChange={(e) => field.onChange(e.target.checked)}
                  data-testid="sms-consent-checkbox"
                  style={checkboxStyle}
                />
              )}
            />
          )}
        />

        <Controller
          name="marketingConsent"
          render={({ field }) => (
            <FormControlLabel
              style={{ marginBottom: 24, alignItems: 'flex-start' }}
              label={(
                <MarketingConsentLabel>
                  {t('coverage.spring_health.confirm_details.marketing_consent')}
                </MarketingConsentLabel>
              )}
              control={(
                <Checkbox
                  checked={!!field.value}
                  onChange={(e) => field.onChange(e.target.checked)}
                  data-testid="marketing-consent-checkbox"
                  style={checkboxStyle}
                />
              )}
            />
          )}
        />
      </CheckboxContainer>

      <SubmitButtonWrapper>
        <Button
          variant="contained"
          type="submit"
          color="primary"
          data-testid="submit-button"
          disabled={!isValid || loading}
          loading={loading}
          rounded
          onClick={handleSubmit(onSubmit)}
          style={{
            ...submitButtonStyle,
            backgroundColor: (!isValid || loading) ? undefined : '#111',
            color: '#FFF',
          }}
        >
          {t('coverage.spring_health.confirm_details.submit_button')}
        </Button>
      </SubmitButtonWrapper>
    </FormContainer>
  );
};

export default ConfirmDetails;
